import { supabase } from "@/integrations/supabase/client";
import type { Library, Profile } from "@/types/database";
import { getSafeRandomId } from "@/lib/random-id";

export type CachedOrderRecord = {
  id: string;
  request_code: string;
  status: "pending" | "accepted" | "student_confirmed" | "approved" | "rejected" | "cancelled" | "canceled" | "disputed";
  qr_token: string | null;
  qr_redeemed_at: string | null;
  created_at: string;
  updated_at: string;
  resource_id: string | null;
  bundle_id?: string | null;
  library_id: string;
  resources?: { title: string; includes_print_qr_code?: boolean | null } | null;
  bundles?: { title: string } | null;
  libraries?: { name: string } | null;
};

export type CachedStudentStats = {
  accessed: number;
  followed: number;
  saved: number;
  updated_at: string;
};

type CachedProfile = Profile & {
  sync_status: "synced" | "pending";
};

type OfflineAccountJob = {
  id: string;
  key: string;
  userId: string;
  type: "profile-upsert";
};

type OfflineAccountUserState = {
  profile: CachedProfile | null;
  library: Library | null;
  orders: CachedOrderRecord[];
  studentStats: CachedStudentStats | null;
  queue: OfflineAccountJob[];
};

type OfflineAccountState = {
  users: Record<string, OfflineAccountUserState>;
};

const STORAGE_KEY = "athr.offline-account.v1";
const STORAGE_UPDATED_EVENT = "athr-offline-account-updated";

const createEmptyUserState = (): OfflineAccountUserState => ({
  profile: null,
  library: null,
  orders: [],
  studentStats: null,
  queue: [],
});

const readState = (): OfflineAccountState => {
  if (typeof window === "undefined") {
    return { users: {} };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as OfflineAccountState) : { users: {} };
  } catch {
    return { users: {} };
  }
};

const writeState = (state: OfflineAccountState) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    window.dispatchEvent(new CustomEvent(STORAGE_UPDATED_EVENT));
  } catch {
    // Ignore storage failures.
  }
};

const mutateState = <T>(updater: (state: OfflineAccountState) => T): T => {
  const state = readState();
  const result = updater(state);
  writeState(state);
  return result;
};

const getUserState = (state: OfflineAccountState, userId: string) => {
  if (!state.users[userId]) {
    state.users[userId] = createEmptyUserState();
  }
  return state.users[userId];
};

const profileJobKey = (userId: string) => `profile-upsert:${userId}`;

const createFallbackProfile = (userId: string, current: CachedProfile | null, input: Partial<Profile>): CachedProfile => ({
  id: userId,
  email: current?.email ?? input.email ?? null,
  full_name: current?.full_name ?? input.full_name ?? null,
  username: current?.username ?? input.username ?? null,
  phone: current?.phone ?? input.phone ?? null,
  account_type: current?.account_type ?? input.account_type ?? "student",
  publisher_status: current?.publisher_status ?? input.publisher_status ?? null,
  onboarding_completed: current?.onboarding_completed ?? input.onboarding_completed ?? false,
  role: current?.role ?? input.role ?? "student",
  university: current?.university ?? input.university ?? null,
  faculty: current?.faculty ?? input.faculty ?? null,
  enrollment_year: current?.enrollment_year ?? input.enrollment_year ?? null,
  banned: current?.banned ?? input.banned ?? false,
  avatar_url: current?.avatar_url ?? input.avatar_url ?? null,
  created_at: current?.created_at ?? input.created_at ?? new Date().toISOString(),
  sync_status: current?.sync_status ?? "synced",
});

export const getOfflineAccountEventName = () => STORAGE_UPDATED_EVENT;

export const getOfflineProfile = (userId: string) => readState().users[userId]?.profile ?? null;

export const hydrateOfflineProfile = (profile: Profile | null) => {
  if (!profile) return;

  mutateState((state) => {
    const userState = getUserState(state, profile.id);
    const current = userState.profile;
    userState.profile = {
      ...profile,
      ...(current?.sync_status === "pending" ? current : {}),
      sync_status: current?.sync_status === "pending" ? "pending" : "synced",
    };
  });
};

export const saveLocalProfileDraft = (profile: Partial<Profile> & Pick<Profile, "id">) =>
  mutateState((state) => {
    const userState = getUserState(state, profile.id);
    const nextProfile: CachedProfile = {
      ...createFallbackProfile(profile.id, userState.profile, profile),
      ...profile,
      sync_status: "pending",
    };
    userState.profile = nextProfile;
    userState.queue = [
      ...userState.queue.filter((job) => job.key !== profileJobKey(profile.id)),
      {
        id: getSafeRandomId(),
        key: profileJobKey(profile.id),
        userId: profile.id,
        type: "profile-upsert",
      },
    ];
    return nextProfile;
  });

export const getOfflineLibrary = (userId: string) => readState().users[userId]?.library ?? null;

export const hydrateOfflineLibrary = (userId: string, library: Library | null) => {
  mutateState((state) => {
    const userState = getUserState(state, userId);
    userState.library = library;
  });
};

export const getOfflineOrders = (userId: string) => readState().users[userId]?.orders ?? [];

export const hydrateOfflineOrders = (userId: string, orders: CachedOrderRecord[]) => {
  mutateState((state) => {
    const userState = getUserState(state, userId);
    userState.orders = orders;
  });
};

export const getOfflineStudentStats = (userId: string) => readState().users[userId]?.studentStats ?? null;

export const hydrateOfflineStudentStats = (userId: string, stats: CachedStudentStats) => {
  mutateState((state) => {
    const userState = getUserState(state, userId);
    userState.studentStats = stats;
  });
};

export const getOfflineAccountQueueCount = (userId?: string) => {
  const state = readState();
  if (userId) {
    return state.users[userId]?.queue.length ?? 0;
  }
  return Object.values(state.users).reduce((sum, userState) => sum + userState.queue.length, 0);
};

const markProfileSynced = (userId: string) => {
  mutateState((state) => {
    const userState = getUserState(state, userId);
    if (userState.profile) {
      userState.profile = {
        ...userState.profile,
        sync_status: "synced",
      };
    }
    userState.queue = userState.queue.filter((job) => job.key !== profileJobKey(userId));
  });
};

export const flushOfflineAccountQueue = async () => {
  if (typeof navigator !== "undefined" && !navigator.onLine) return;

  const jobs = Object.values(readState().users).flatMap((userState) => userState.queue);
  if (!jobs.length) return;

  for (const job of jobs) {
    try {
      if (job.type !== "profile-upsert") continue;
      const profile = getOfflineProfile(job.userId);
      if (!profile) {
        markProfileSynced(job.userId);
        continue;
      }

      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: profile.full_name,
          username: profile.username,
          university: profile.university,
          faculty: profile.faculty,
          enrollment_year: profile.enrollment_year,
          phone: profile.phone,
          updated_at: new Date().toISOString(),
        })
        .eq("id", job.userId);

      if (error) continue;
      markProfileSynced(job.userId);
    } catch {
      // Leave queued for retry.
    }
  }
};
